import { envType } from '@atlassiansox/analytics-web-client';

import { fg } from '@atlaskit/platform-feature-flags';

import { ALL_SUPPORTED_OBJECTS } from './constants';
import type { GlobalObjectType } from './types';

// Helper function to check if we're in an iframe - exported for testing
export const isInIframe = (): boolean => {
	try {
		return window !== window.top;
	} catch {
		// If we can't access window.top due to cross-origin restrictions, assume we're in an iframe
		return true;
	}
};

export const asGlobalObjectType = (value: string): GlobalObjectType => {
	if (!ALL_SUPPORTED_OBJECTS.includes(value as GlobalObjectType)) {
		throw new Error(`Unsupported global object type: ${value}`);
	}

	return value as GlobalObjectType;
};

/**
 * Constructs a proper embed URL by extracting query parameters and reconstructing the URL
 * @param url - The original URL
 * @param supportedObjects - Array of supported object types for previewPanels param
 * @returns A properly formatted embed URL with all query parameters at the end
 */
export const buildIssueViewEmbedUrl = (
	url: string,
	supportedObjects: string[],
	rootSourceScreen?: string,
): string => {
	try {
		const urlObj = new URL(url);

		if (fg('pol-21166-fix-404-on-jpd-preview')) {
			if (isJpdObject(urlObj)) {
				return buildJpdEmbedUrl(urlObj);
			}
		}

		// Ensure there's no trailing slash before appending '/embed'
		urlObj.pathname = urlObj.pathname.replace(/\/$/, '') + '/embed';

		// Add previewPanels to existing search params
		urlObj.searchParams.set('previewPanels', supportedObjects.join(','));

		if (rootSourceScreen && fg('add_capacity_tab_to_the_board_horizontal_nav')) {
			urlObj.searchParams.set('previewRootSourceScreen', rootSourceScreen);
		}
		// Return the full URL as a string
		return urlObj.toString();
	} catch {
		// Fallback for invalid URLs
		if (fg('add_capacity_tab_to_the_board_horizontal_nav')) {
			const encodedRootSource = rootSourceScreen
				? `&previewRootSource=${encodeURIComponent(rootSourceScreen)}`
				: '';
			return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}${encodedRootSource}`;
		} else {
			return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}`;
		}
	}
};

export const getAnalyticsEnv = (hostname: string) => {
	if (hostname.includes('localhost') || hostname.includes('jira-dev')) {
		return envType.DEV;
	}

	return envType.PROD;
};

const isJpdObject = (urlObj: URL) => {
	return urlObj.pathname.includes('/jira/polaris/projects/');
};

const buildJpdEmbedUrl = (urlObj: URL) => {
	const ideaKey = urlObj.searchParams.get('selectedIssue');
	urlObj.searchParams.delete('selectedIssue');

	urlObj.pathname = `/browse/${ideaKey}/embed`;

	return urlObj.toString();
};
